import { Injectable, inject } from '@angular/core';
import { Observable, Subscription, from, interval, merge, of, timer } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, takeWhile, timeout } from 'rxjs/operators';
import { SPREAD_PARSE_WAIT_MS, type DesignPackageSpread } from '@lite-workspace/dto-design-system';
import { SpreadState } from '../entities/design.models';
import { SocketService, SocketRoom, SocketEvent, type DesignPsdParseData } from '../../../../core/socket.service';
import { DesignsApiService } from './designs-api.service';

/** Local on purpose: here the poll is the FALLBACK to the WS event, so its cadence answers to the socket,
 *  not to the editor's poll-only loop. The deadline is the shared number — that one is about the backend. */
const POLL_INTERVAL_MS = 4000;
/** Budget for ONE poll read — generous next to the interval, short next to the deadline. */
const READ_TIMEOUT_MS = 20_000;
/** How long our own reads may keep failing before the spinner becomes a lie and the user gets the retry. */
const UNREADABLE_GRACE_MS = 90_000;

const TERMINAL_STATES: ReadonlySet<SpreadState> = new Set([SpreadState.REJECTED, SpreadState.FAILED]);

/** One spread's settled outcome, before it is keyed by id. */
type SpreadOutcome = Omit<SpreadParseResult, 'spreadId'>;

/** A parsed-spread outcome: the re-fetched spread once the server finished (boxes populated) or failed. */
export interface SpreadParseResult {
  readonly spreadId: string;
  readonly spread: DesignPackageSpread | null;
  readonly failed: boolean;
  /** The spread gave way to others (INDD conversion) or is simply gone — an outcome, not a failure. */
  readonly replaced?: boolean;
  /** The spreads that took its place, already under watch. */
  readonly replacedBy?: readonly string[];
  /** Only the WS payload knows who broke it; the poll fallback leaves it unset (defaults downstream). */
  readonly errorKind?: 'file' | 'system';
}

interface SpreadFetch {
  spread: DesignPackageSpread | null;
  gone: boolean;
}

/** No spread and not a 404: our own read came back empty-handed, which is no news about the parse. */
const UNREADABLE: SpreadFetch = { spread: null, gone: false };

function isNotFound(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 404;
}

/** A spread with parsed boxes, a terminally failed one, or one that is gone — anything else is still owed. */
function isSettled({ spread, gone }: SpreadFetch): boolean {
  if (gone) return true;
  if (!spread) return false;
  const parsed = Array.isArray(spread.model?.boxes) && spread.model.boxes.length > 0;
  return parsed || TERMINAL_STATES.has(spread.state);
}

function outcomeOf({ spread, gone }: SpreadFetch): SpreadOutcome {
  return { spread, failed: !gone && !!spread && TERMINAL_STATES.has(spread.state), replaced: gone, errorKind: undefined };
}

/**
 * Watches server-side PSD parsing for a set of spreads. A spread is done when its `model.boxes` is
 * populated (server preview + parsed layout), it reaches a terminal failure state, it is replaced by
 * the spreads an INDD conversion produced, or the deadline runs out — every path settles, none hangs.
 * Progress arrives over WS (room `design:package:spread:<designPackageId>`, event `designPsdParse`,
 * demuxed per spread by the payload's `spreadId`); a poll is the fallback if the WS message is missed.
 */
@Injectable({ providedIn: 'root' })
export class DesignParseWatcher {
  private readonly socket = inject(SocketService);
  private readonly api = inject(DesignsApiService);

  watch(designId: string, spreadIds: string[]): Observable<SpreadParseResult> {
    return new Observable<SpreadParseResult>(subscriber => {
      this.socket.connect();
      const pending = new Set<string>();
      const subs = new Subscription();
      const deadline = Date.now() + SPREAD_PARSE_WAIT_MS;

      const settle = (spreadId: string, outcome: SpreadOutcome): void => {
        if (!pending.has(spreadId)) return;
        pending.delete(spreadId);
        subscriber.next({ spreadId, ...outcome });
        if (pending.size === 0) subscriber.complete();
      };

      // The server emits to the design-package room keyed by designPackageId (not per spread); the
      // per-spread demux is the payload's spreadId. Join once and fan out to the pending spreads.
      const room$ = this.socket.joinRoom(SocketRoom.DESIGN_PACKAGE_SPREAD, designId).pipe(
        filter(event => event.eventType === SocketEvent.DESIGN_PSD_PARSE),
        map(event => event.data as DesignPsdParseData),
      );

      const fetchSpread = async (spreadId: string): Promise<SpreadFetch> => {
        try {
          return { spread: await this.api.loadSpread(designId, spreadId), gone: false };
        } catch (error) {
          // A spread that no longer exists will never parse — waiting for it is waiting forever.
          return isNotFound(error) ? { spread: null, gone: true } : UNREADABLE;
        }
      };

      const watchSpread = (spreadId: string): void => {
        if (pending.has(spreadId)) return;
        pending.add(spreadId);
        let unreadableSince: number | null = null;

        // One rule for both paths: null means the spread is still owed an answer, so the watch stays up.
        const verdictOf = (fetched: SpreadFetch): SpreadOutcome | null => {
          if (isSettled(fetched)) {
            unreadableSince = null;
            return outcomeOf(fetched);
          }

          if (fetched.spread) {
            unreadableSince = null;
            return null;
          }

          unreadableSince ??= Date.now();
          // Reads failing this long are our side being down: the gate must not stay shut to the deadline.
          const givingUp = Date.now() - unreadableSince >= UNREADABLE_GRACE_MS;
          return givingUp ? { spread: null, failed: true, errorKind: undefined } : null;
        };

        const ws$ = room$.pipe(
          filter(data => data.spreadId === spreadId && data.status !== 'replaced'),
          switchMap(async (data): Promise<SpreadOutcome | null> => {
            if (data.status === 'error') return { spread: null, failed: true, errorKind: data.errorKind };
            return verdictOf(await fetchSpread(spreadId));
          }),
        );

        const poll$ = interval(POLL_INTERVAL_MS).pipe(
          takeWhile(() => pending.has(spreadId) && Date.now() < deadline),
          // exhaustMap, not switchMap: a read slower than the tick must still LAND, not be cancelled.
          exhaustMap(() =>
            // Waiting on the inner read is what makes a hang fatal — bound it, and keep the release local:
            // a thrown TimeoutError would tear down the whole watch.
            from(fetchSpread(spreadId)).pipe(
              timeout(READ_TIMEOUT_MS),
              catchError(() => of(UNREADABLE)),
              map(verdictOf),
            ),
          ),
        );

        subs.add(
          merge(ws$, poll$)
            .pipe(filter((outcome): outcome is SpreadOutcome => outcome !== null))
            .subscribe(({ spread, failed, replaced, errorKind }) => settle(spreadId, { spread, failed, replaced, errorKind })),
        );
      };

      // INDD conversion swaps the uploaded placeholder for one spread per artboard: take the new ones
      // under watch BEFORE releasing the old one, or the wait would end while the real work continues.
      subs.add(
        room$.pipe(filter(data => data.status === 'replaced')).subscribe(data => {
          if (!pending.has(data.spreadId)) return;
          const replacedBy = data.replacedBy ?? [];
          for (const id of replacedBy) watchSpread(id);
          settle(data.spreadId, { spread: null, failed: false, replaced: true, replacedBy });
        }),
      );

      // The poll's own deadline only stops the polling; without this the pending set never empties
      // and the caller keeps waiting on a stream that will never complete.
      subs.add(
        timer(SPREAD_PARSE_WAIT_MS).subscribe(() => {
          for (const spreadId of [...pending]) settle(spreadId, { spread: null, failed: true });
        }),
      );

      for (const spreadId of spreadIds) watchSpread(spreadId);

      // The design-package room subject is shared across concurrent watchers of the same design, so
      // tearing it down here would sever a sibling watcher; unsubscribing this watcher's streams is
      // enough to stop delivery. The room stays joined (idle after parse) for the session.
      return () => subs.unsubscribe();
    });
  }
}
